import { IPredicate } from '@src/models/api';
import { AnyType } from '@src/models/common';
import { useEffect, useState } from 'react';
import { TControllerValue, TFilterScheme } from './models';
import { FiltersController } from './FiltersController';

import { Subscription } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

export const FILTER_CONTROLLER_EVENTS = {
  /** Изменение общей схемы */
  SCHEME: 'internal_scheme',

  /** Общий список фильтров */
  VALUE_LIST: 'internal_value_list',
} as const;

type TEvent<T> = T extends infer P
  ? P extends 'internal_scheme'
    ? Map<string, TFilterScheme>
    : P extends 'internal_value_list'
    ? Map<string, TControllerValue>
    : P extends string
    ? TControllerValue
    : IPredicate[]
  : AnyType;

/** Позваляет синхронизировать локальное состояние реакта и значение из контроллера,
 *
 * Здесь `event` используется как имя фильтра или евенты из константы `FILTER_CONTROLLER_EVENTS` для подписки на какие то общие изменения в списках
 *
 * Если мы его не указываем отдается общее значение фильтров
 */
export const useSyncFilterValue = <T extends string | undefined = undefined>({
  controller,
  event,
}: {
  controller: FiltersController;
  event?: T;
}): TEvent<T> => {
  let defaultValue: AnyType = null;

  switch (event) {
    case undefined:
      defaultValue = controller.predicate();
      break;

    case FILTER_CONTROLLER_EVENTS.VALUE_LIST:
      defaultValue = controller.getValueList();
      break;

    case FILTER_CONTROLLER_EVENTS.SCHEME:
      defaultValue = controller.getScheme();
      break;

    default:
      defaultValue = controller.getValueList().get(event);
      break;
  }

  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    let subscription: Subscription | undefined = undefined;

    switch (event) {
      case undefined:
        subscription = controller.valueList$.subscribe(() => {
          setValue(controller.predicate());
        });
        break;

      case FILTER_CONTROLLER_EVENTS.SCHEME:
        subscription = controller.scheme$.subscribe(setValue);
        break;

      case FILTER_CONTROLLER_EVENTS.VALUE_LIST:
        subscription = controller.valueList$.subscribe(setValue);
        break;

      default:
        if (typeof event === 'string') {
          subscription = controller.valueList$
            .pipe(
              map((v) => v.get(event)),
              distinctUntilChanged(),
            )
            .subscribe(setValue);
        }
        break;
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [controller, event]);

  return value;
};
